import { useState, useEffect, useCallback } from "react";
import { useRouter } from "@tanstack/react-router";
import { TransactionHistoryService } from "../../services/api/transactionHistory.service";
import SummaryCards from "./SummaryCards";
import BifurcationSection from "./BifurcationSection";
import TransactionTabs from "./TransactionTabs";
import AddExpenseModal from "./AddExpenseModal";
import LogDepositModal from "./LogDepositModal";
import MonthEndLogModal from "./MonthEndLogModal";
import { PlusCircle, Building2, CalendarCheck } from "lucide-react";
import {
  DateRange,
  PaymentSummaryData,
  PaymentOrderItem,
  AppointmentPaymentItem,
} from "../../types/transactionHistory.types";

interface ClinicTransactionViewProps {
  dateRange: DateRange;
  clinicId?: string;
  clinicName?: string;
  isAdmin?: boolean;
  onBack?: () => void;
}

export default function ClinicTransactionView({
  dateRange,
  clinicId,
  clinicName,
  isAdmin = false,
  onBack,
}: ClinicTransactionViewProps) {
  const router = useRouter();
  const [summaryData, setSummaryData] = useState<PaymentSummaryData | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showLogDeposit, setShowLogDeposit] = useState(false);
  const [showMonthEndLog, setShowMonthEndLog] = useState(false);

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const res = await TransactionHistoryService.getPaymentSummary({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        clinicId,
      });
      if (res.success) {
        setSummaryData(res.data);
      }
    } catch (err) {
      console.error("Error fetching summary:", err);
    } finally {
      setSummaryLoading(false);
    }
  }, [dateRange, clinicId]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  const handleOrderClick = (item: any) => {
    const basePath = isAdmin ? "/admin" : "/clinic";

    // Check item type and navigate to existing detail pages
    const appointmentId = (item as any)?.appointmentId || (item as any)?.appointment_id;
    if (appointmentId) {
      router.navigate({ to: `${basePath}/appointments/${appointmentId}` });
      return;
    }

    const orderType = (item as any)?.orderType || (item as any)?.order_type || (item as any)?.type;
    if (orderType) {
      const routeMap: Record<string, string> = {
        "online-order": `${basePath}/stocks-orders/online-orders`,
        "offline-order": `${basePath}/stocks-orders/offline-orders`,
        "labtest-order": `${basePath}/stocks-orders/labtest-orders`,
        "offline-labtest": `${basePath}/stocks-orders/offline-labtest-orders`,
        "online-return": `${basePath}/stocks-orders/online-orders`,
        "offline-return": `${basePath}/stocks-orders/offline-orders`,
      };
      const route = routeMap[orderType];
      if (route) {
        router.navigate({ to: route });
      }
    }
  };

  const handleExpenseSuccess = () => {
    fetchSummary();
  };

  return (
    <div className="space-y-5">
      {/* Header with back + clinic name (for admin drill-down) */}
      {isAdmin && clinicName && (
        <div className="flex items-center gap-2">
          <button
            onClick={onBack}
            className="text-xs text-secondary hover:underline font-medium"
          >
            ← Back to Clinics
          </button>
          <span className="text-gray-300">/</span>
          <span className="text-sm font-medium text-gray-700">{clinicName}</span>
        </div>
      )}

      {/* Summary section */}
      <SummaryCards
        data={summaryData?.overviewCards || null}
        loading={summaryLoading}
        heading={isAdmin && clinicName ? "Clinic Overview" : "Summary"}
      />

      {/* Bifurcation */}
      <BifurcationSection
        bifurcation={summaryData?.bifurcation || null}
        loading={summaryLoading}
      />

      {/* Action buttons: Add New Expense | Deposit Log | Month End Log */}
      <div className="flex items-center justify-between">
        <div /> {/* spacer */}
        <div className="flex items-center gap-2.5">
          <button
            onClick={() => setShowAddExpense(true)}
            className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-all cursor-pointer shadow-sm"
          >
            <PlusCircle className="w-3.5 h-3.5" />
            <span>Add New Expense</span>
          </button>
          <button
            onClick={() => setShowLogDeposit(true)}
            className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-all cursor-pointer shadow-sm"
          >
            <Building2 className="w-3.5 h-3.5" />
            <span>Deposit Log</span>
          </button>
          <button
            onClick={() => setShowMonthEndLog(true)}
            className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-secondary bg-secondary/10 border border-secondary/25 rounded-lg hover:bg-secondary/20 transition-all cursor-pointer shadow-sm"
          >
            <CalendarCheck className="w-3.5 h-3.5" />
            <span>Month End Log</span>
          </button>
        </div>
      </div>

      <TransactionTabs
        dateRange={dateRange}
        clinicId={clinicId}
        onOrderClick={handleOrderClick}
      />

      {/* Modals */}
      <AddExpenseModal
        isOpen={showAddExpense}
        onClose={() => setShowAddExpense(false)}
        onSuccess={handleExpenseSuccess}
        clinicId={clinicId}
        isAdmin={isAdmin}
      />

      <LogDepositModal
        isOpen={showLogDeposit}
        onClose={() => setShowLogDeposit(false)}
        onSuccess={handleExpenseSuccess}
        clinicId={clinicId}
      />

      <MonthEndLogModal
        isOpen={showMonthEndLog}
        onClose={() => setShowMonthEndLog(false)}
        clinicId={clinicId}
        clinicName={clinicName}
        isAdmin={isAdmin}
      />
    </div>
  );
}

